import { CustomSlider } from '@/components/CustomSlider'
import { InputGroup } from '@/components/InputGroup'
import { SliderLabel } from '@/components/ui/SliderLabel'
import CompaniesSelection from '@/containers/forms/CompaniesSelection'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormRow } from '@/containers/forms/FormRow'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useInsurers, useRiskData, useSelections } from '@/hooks'
import { useGetInsuranceParamsDiscounts } from '@/hooks/useInsuranceParams'
import { formatLicensePlate } from '@/utils/formatLicencePlate'
import { FormControl, FormHelperText, FormLabel, Grid, Select, Stack, Switch } from '@chakra-ui/react'
import { format, parse } from 'BrokerTsLib/src/Utils/formatInputs'
import type { InsuranceParamsTravelDto, InsurerDiscount } from 'BrokerTsLib/src/dtos'
import { Insurer, Risk, RiskVariant, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

export const ParametersForm = ({ onDiscountsLoaded }: { onDiscountsLoaded: (discounts: InsurerDiscount[]) => void }) => {

  const methods = useFormContext<InsuranceParamsTravelDto>()
  const {
    setValue,
    getValues,
    watch,
    register,
    formState: { errors }
  } = methods

  const { insuranceParams: ip, insuranceParamsId, isDisabled } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsTravelDto

  const destinationId = insuranceParams.travel.destination
  const range = watch('travel.range')
  const allowVA = watch('addons.vehicleAssistanceAvailable')
  const allowCovid = watch('addons.covidInsuranceAvailable')
  const assistanceVehicle = watch('addons.vehicleAssistanceRequired')
  const stornoInsurance = watch('addons.stornoInsuranceRequired')
  const selectedInsurers = watch('baseParams.insurers')

  const { data: allInsurersData } = useInsurers(false, false)
  const allInsurers = allInsurersData?.data ?? []

  const [insurersDiscounts, setInsurersDiscounts] = useState<InsurerDiscount[] | undefined>(undefined)
  useGetInsuranceParamsDiscounts(insuranceParamsId!, { enabled: !!selectedInsurers }, (data) => {
    if (data) {
      setInsurersDiscounts(data)
      setValue('baseParams.insurerDiscounts', data?.filter(x => selectedInsurers!.includes(x.insurerId)) ?? [])
    }
    onDiscountsLoaded(data ?? [])
  })

  useEffect(() => {
    // reset ve formulari, pokud se zmeni pocatek nebo konec pojisteni
    setValue('generalParams.insuranceStartDate', insuranceParams?.generalParams.insuranceStartDate)
    setValue('travel.insuranceEndDate', insuranceParams?.travel.insuranceEndDate)
  }, [insuranceParams?.generalParams.insuranceStartDate, insuranceParams?.travel.insuranceEndDate])

  useEffect(() => {
    setValue('baseParams.insurers', [...allInsurers.map(({ id }) => id) ?? []])
  }, [setValue])

  useEffect(() => {
    if ((allInsurers.length ?? 0) > 0 && getValues('baseParams.insurers')?.length == 0)
      setValue('baseParams.insurers', allInsurers.map(({ id }) => id))
  }, [allInsurers.length])


  const { selected: selectedRozsah, value: rozsah, sliderDS: rozsahSliderDS, min: rozsahMin, max: rozsahMax } = useRiskData(
    Risk.TRAVEL_RANGE,
    [{ riskVariantId: range, riskId: Risk.TRAVEL_RANGE }],
    [...(destinationId != Selection.TravelDestinationCzech ? [RiskVariant.TravelRangeAll] : [])]
  )

  // v pripade destinace CZ je rozsah vzdy TravelRangeAll
  useEffect(() => {
    if (destinationId == Selection.TravelDestinationCzech)
      setValue('travel.range', RiskVariant.TravelRangeAll)
    else if (!range)
      setValue('travel.range', RiskVariant.TravelRangeMiddle)
  }, [destinationId])

  const firstRegistrationDateNeeded =
    ((allInsurers?.findIndex(x => x.id == Insurer.Colonnade) ?? -1) > -1) ||
    ((allInsurers?.findIndex(x => x.id == Insurer.Kooperativa) ?? -1) > -1) ||
    ((allInsurers?.findIndex(x => x.id == Insurer.AxaAssistance) ?? -1) > -1)

  const [holidayPriceString, setHolidayPriceString] = useState(format(getValues('addons.holidayPrice')?.toString()))
  const [holidayPricePerPersonString, setHolidayPricePerPersonString] = useState(format(getValues('addons.holidayPricePerPerson')?.toString()))

  const handleHolidayPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    const parsed = parse(value)
    const formatted = format(parsed)

    setHolidayPriceString(formatted)
    setValue('addons.holidayPrice', parseInt(parsed))

    if (!Number.isNaN(parseInt(parsed))) {
      const insureesCount = insuranceParams.travel.insurees?.length ?? 1

      const valuePerPerson = Math.round(parseInt(parsed) / insureesCount)
      setValue('addons.holidayPricePerPerson', valuePerPerson)
      setHolidayPricePerPersonString(format(valuePerPerson.toString()))
    }
  }

  const handleHolidayPricePerPersonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    const parsed = parse(value)
    const formatted = format(parsed)

    setHolidayPricePerPersonString(formatted)
    setValue('addons.holidayPricePerPerson', parseInt(parsed))
  }

  const { data: dataPaymentState, isLoading: isLoadingPaymentState, isFetching: isFetchingPaymentState } = useSelections(SelectionType.TravelPaymentState)
  const paymentStateNeeded = (allInsurers?.findIndex(x => x.id == Insurer.CPP) ?? -1) > -1

  return (
    <>
      <FormRow>
        {rozsahSliderDS && rozsahMin && rozsahMax &&
          <FormControl sx={{ gap: 4 }}>
            <SliderLabel
              label="Rozsah pojištění"
              value={rozsah?.name} />

            <CustomSlider
              value={rozsahSliderDS.find(x => x.id == selectedRozsah?.riskVariantId)?.i ?? 0}
              labels={[
                { label: rozsahMin?.name ?? '', value: rozsahMin.i },
                { label: rozsahMax?.name ?? '', value: rozsahMax.i },
              ]}
              min={0}
              isDisabled={destinationId == Selection.TravelDestinationCzech || isDisabled}
              max={rozsahSliderDS.length - 1}
              step={1}
              onChange={(val) => {
                setValue('travel.range', rozsahSliderDS.find(x => x.i == val)?.id)
              }} />
          </FormControl>
        }
      </FormRow>

      <FormRow itemsCount={3}>

        {/* asistence vozidla */}
        {allowVA &&
          <Grid sx={{ gap: 4 }}>
            <FormControl>
              <FormLabel gap={4}>
                <Switch
                  {...register('addons.vehicleAssistanceRequired')}
                  disabled={isDisabled}
                  dir="rtl"
                  size='lg'
                />

                Asistence vozidla
              </FormLabel>


              <FormHelperText>
                Pojištění asistenčních služeb vozidla na zvolenou cestu
              </FormHelperText>
            </FormControl>

            {assistanceVehicle && (
              <FormControl isInvalid={!!errors.addons?.vehicleRegistrationMark} isRequired gridColumn='0.5'>
                <FormLabel htmlFor="addons.vehicleRegistrationMark">
                  Reg. značka (SPZ)
                </FormLabel>

                <InputGroup
                  id="addons.vehicleRegistrationMark"
                  placeholder="SPZ"
                  htmlSize={8}
                  isDisabled={isDisabled}
                  autoComplete="off"
                  errors={errors.addons?.vehicleRegistrationMark}
                  value={formatLicensePlate(watch('addons.vehicleRegistrationMark'))}
                  {...register('addons.vehicleRegistrationMark', {
                    pattern: /[a-zA-Z]{6,8}/gi,
                    minLength: 6,
                    onChange: (e) => e.target.value = formatLicensePlate(e.target.value) ?? ''
                    // onChange: (e) => setValue('addons.vehicleRegistrationMark', e.target.value.toUpperCase())
                  })}
                />

                <FormHelperText>
                  Zadejte SPZ pojišťovaného vozidla
                </FormHelperText>
              </FormControl>
            )}
            {assistanceVehicle && firstRegistrationDateNeeded && (
              <FormControl isInvalid={!!errors.addons?.vehicleFirstRegistrationDate} gridColumn='0.5'>
                <FormLabel htmlFor="addons.vehicleFirstRegistrationDate">
                  Datum první registrace
                </FormLabel>

                <Datepicker
                  isDisabled={isDisabled}
                  name="addons.vehicleFirstRegistrationDate"
                />

                <FormHelperText>
                  Zadejte datum první registrace vozidla
                </FormHelperText>
              </FormControl>
            )}
          </Grid>
        }

        {/* covid */}
        {allowCovid &&
          <FormControl>
            <FormLabel gap={4}>
              <Switch
                {...register('addons.covidInsuranceRequired')}
                disabled={isDisabled}
                dir="rtl"
                size='lg'
              />

              COVID
            </FormLabel>

            <FormHelperText>
              Pojištění Covid-19 proplatí náklady v případě onemocnění v zahraničí (léky, ošetření, hospitalizaci…)
            </FormHelperText>
          </FormControl>
        }


        {/* pojisteni stornopoplatku */}
        <Stack sx={{ gap: 4 }}>
          <FormControl>
            {/* <FormLabel gap={4} justifyContent='space-between' w='100%'> */}
            <FormLabel gap={4} >
              <Switch
                {...register('addons.stornoInsuranceRequired')}
                disabled={isDisabled}
                dir="rtl"
                size='lg'
              />

              Pojištění stornopoplatku
            </FormLabel>

            <FormHelperText>
              Pojištění nákladů na stornopoplatky za objednanané služby
            </FormHelperText>
          </FormControl>

          {stornoInsurance && (
            <>
              {/* <FormRow itemsCount={2} gridColumn='1 / span 2' sx={{ rowGap: 4 }}> */}

              <FormControl isInvalid={!!errors.addons?.holidayPrice} isRequired>
                <FormLabel htmlFor={'addons.holidayPrice'}>
                  Celková cena zájezdu
                </FormLabel>

                <InputGroup
                  id={'addons.holidayPrice'}
                  placeholder="Např. 50 000"
                  type="string"
                  htmlSize={12}
                  isDisabled={isDisabled}
                  // min="0"
                  autoComplete="off"
                  // inputMode="decimal"
                  rightElement="Kč"
                  errors={errors.addons?.holidayPrice}
                  value={holidayPriceString}
                  onChange={handleHolidayPriceChange}
                // onBlur={handleHolidayPriceOnBlur}
                />
              </FormControl>

              <FormControl isInvalid={!!errors.addons?.holidayPricePerPerson} isRequired>
                <FormLabel htmlFor={'addons.holidayPricePerPerson'}>Cena zájezdu za 1 osobu</FormLabel>

                <InputGroup
                  id={'addons.holidayPricePerPerson'}
                  placeholder="Např. 20 000"
                  type="string"
                  htmlSize={12}
                  isDisabled={isDisabled}
                  // min="0"
                  autoComplete="off"
                  // inputMode="decimal"
                  rightElement="Kč"
                  errors={errors.addons?.holidayPricePerPerson}
                  value={holidayPricePerPersonString}
                  onChange={handleHolidayPricePerPersonChange}
                />
              </FormControl>

              <FormControl isInvalid={!!errors.addons?.holidayPurchaseDate} isRequired>
                <FormLabel htmlFor="addons.holidayPurchaseDate">Datum zakoupení zájezdu</FormLabel>
                <Datepicker
                  isDisabled={isDisabled}
                  name="addons.holidayPurchaseDate"
                />
              </FormControl>

              {paymentStateNeeded && (
                <FormControl isInvalid={!!errors.addons?.holidayPaymentState} isRequired>
                  <FormLabel htmlFor="addons.holidayPaymentState">Stav zájezdu</FormLabel>
                  <Select
                    defaultValue={getValues('addons.holidayPaymentState')?.toString() ?? ''}
                    value={watch('addons.holidayPaymentState')?.toString() ?? ''}
                    isDisabled={isDisabled}
                    onChange={(e) => setValue('addons.holidayPaymentState', parseInt(e.target.value))}
                  >
                    {dataPaymentState?.data &&
                      dataPaymentState?.data.length > 0 &&
                      dataPaymentState?.data.map((item) => (
                        <option key={item.id} value={item.id.toString()}>
                          {item.description}
                        </option>
                      ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </Stack>
      </FormRow>

      <FormRow>
        <CompaniesSelection
          allInsurers={allInsurers}
          allInsurersDiscounts={insurersDiscounts ?? []}
          insurersPath={'baseParams.insurers'}
          insurersDiscountsPath='baseParams.insurerDiscounts'
        />
      </FormRow>
    </>
  )
}
